import axios from 'axios';
import {
	ContactTypeCode,
	TableItemsOptions,
	FamilyRelation,
	FamilyRelationDto,
	ContactDto,
	ContactProfileDto,
	Payment,
	ProfileType,
	ContactProfile,
	FormTypeItem,
	PlanDto,
	NgoUpdateDto,
	Ngo,
	JobEventType,
	VolunteerFunction,
	Center,
	UserRoleTemplate,
	UserPermission,
	UserRoleDto,
	CreateUserDto,
	UpdateUserDto,
	Contact,
	UserAccount,
	UserRole,
	MergeContactDto,
	ContactSearchCondition,
	SearchByAppUserIdCondition,
} from '../../types';
import { tableItemUrl } from '../customisableTable';
import {
	DraftFamilyContactWithRelation,
	InboxMessage,
} from '../../store/types';
import { NgoSignupBasicDto, NgoSignupExtraDto } from '../../types/ngo-signup';
import { District, Region, SubDistrict } from '../../types/location';
import { eventApi } from './event';
import { jobApi } from './job';
import { volunteerApi } from './volunteer';
import { contactApi } from './contact';
import { programApi } from './program';
import { reportApi } from './report';
import { paymentApi } from './payment';
import {
	appendPaymentTableSortTiebreakers,
	appendUpdatedAtTiebreaker,
} from '../tableSortTiebreaker';

interface TableDataResponse<T> {
	result: T[];
	total: number;
}

export interface SalutApi {
	DATA_LENGTH_LIMIT: number;
	fetchProfile(): Promise<any>;
	updateProfile(params: any): Promise<any>;
	job: any;
	contact: any;
	event: any;
	program: any;
}

export interface SalutApiOptions {
	apiUrl: string;
}

export const fetchTableItems = async (
	params: TableItemsOptions,
): Promise<any> => {
	const { data } = await axios.get(tableItemUrl(params));
	return data;
};

export const REFRESH_TOKEN_API_PATH = '/admin/login/refresh-access-token';
export const LOGOUT_API_PATH = '/admin/logout';
export const LOGOUT_FORCE_API_PATH = '/admin/logout/force';
export const NGO_SIGNUP_VERIFY_EMAIL_API_PATH =
	'/contact/ngo-signups/verify-email';
export class SalutApiImpl implements SalutApi {
	DATA_LENGTH_LIMIT = 500;

	event = eventApi;
	job = jobApi;
	volunteer = volunteerApi;
	contact = contactApi;
	program = programApi;
	report = reportApi;
	payment = paymentApi;

	async checkLoginEmailExists(email: string) {
		const { data } = await axios.get(`/admin/check-email-exist/${email}`);
		return data;
	}

	async ngoSignupBasic(ngoSignupBasicData: NgoSignupBasicDto): Promise<Ngo> {
		const { data } = await axios.post(
			`/contact/ngo-signups`,
			ngoSignupBasicData,
		);
		return data;
	}

	async requestEmailVerification(ngoId: number) {
		const { data } = await axios.post(
			`contact/ngo-signups/${ngoId}/request-email-verification`,
		);
		return data;
	}

	async ngoSignupVerifyEmailCode(email: string, code: string) {
		const { data } = await axios.post(NGO_SIGNUP_VERIFY_EMAIL_API_PATH, {
			email,
			code,
		});
		return data;
	}

	async ngoChangeAccountEmail(ngoId: number, email: string) {
		const { data } = await axios.post(
			`/contact/ngo-signups/${ngoId}/change-account-email`,
			{
				email,
			},
		);
		return data;
	}

	async ngoSignupExtra(
		ngoId: number,
		ngoSignupExtraData: NgoSignupExtraDto,
	): Promise<Ngo> {
		const { data } = await axios.put(
			`/contact/ngo-signups/${ngoId}`,
			ngoSignupExtraData,
		);
		return data;
	}

	async checkResetPasswordCode(code: string) {
		const { data } = await axios.get(`/admin/reset-password/${code}`);
		return data;
	}

	async requestResetPassword(email: string) {
		const { data } = await axios.post(`/admin/reset-password/`, {
			email,
		});
		return data;
	}

	async resetPassword(code: string, newPassword: string) {
		const { data } = await axios.put(`/admin/reset-password/${code}`, {
			newPassword,
		});
		return data;
	}

	async suspendUser(id: string, suspend: boolean): Promise<any> {
		const body = {
			id: id,
			suspend: suspend,
		};
		const { data } = await axios.post(`/admin/suspend-user/`, body);
		return data;
	}

	async getNewAccessToken(refreshToken: string): Promise<string> {
		try {
			const {
				data: { accessToken },
			} = await axios.post(REFRESH_TOKEN_API_PATH, { refreshToken });
			return accessToken;
		} catch (e) {
			console.warn(e); // not a big problem
			return '';
		}
	}

	async fetchProfile(): Promise<any> {
		const { data } = await axios.get('/admin/me');
		return data;
	}

	async fetchUnreadInboxMessages(): Promise<any> {
		const { data } = await axios.get(
			'/admin/inbox-messages?isUnread=true&take=5',
		);
		return data;
	}

	async markReadInboxMessage(id: string): Promise<InboxMessage> {
		const { data } = await axios.post(`/admin/inbox-messages/${id}/read`);
		return data;
	}

	async archiveInboxMessage(id: string): Promise<InboxMessage> {
		const { data } = await axios.post(`/admin/inbox-messages/${id}/archive`);
		return data;
	}

	async unarchiveInboxMessage(id: string): Promise<InboxMessage> {
		const { data } = await axios.post(`/admin/inbox-messages/${id}/unarchive`);
		return data;
	}

	async updateProfile(params: any): Promise<any> {
		const { data } = await axios.patch('/admin/me', params);
		return data;
	}

	async getNgo(params: { ngoId: number }): Promise<Ngo> {
		const { data } = await axios.get(`/contact/ngos/${params.ngoId}`);
		return data;
	}

	async updateOnboardingData(params: {
		ngoId: number;
		dto: NgoUpdateDto;
	}): Promise<Ngo> {
		const { data } = await axios.put(
			`/contact/ngos/${params.ngoId}`,
			params.dto,
		);
		return data;
	}

	async submitOnboarding(params: { ngoId: number }): Promise<Ngo> {
		const { data } = await axios.post(
			`/contact/ngos/${params.ngoId}/onboarding`,
		);
		return data;
	}

	async updateTableColumnSettings(params: any): Promise<any> {
		const { data } = await axios.patch('/admin/table-column-settings', params);
		return data;
	}

	async fetchAllContactsItems(params: TableItemsOptions): Promise<any> {
		const url = '/contact/contacts';
		const { sortBy, sortDesc } = appendUpdatedAtTiebreaker(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchAllDuplicateContacts(
		targetContactId: number,
		params: {
			field: string;
			search: string;
		},
	): Promise<Contact[]> {
		const { data } = await axios.get(
			`/contact/contacts/${targetContactId}/duplicates`,
			{
				params,
			},
		);
		return data;
	}

	async mergeContacts(mergeContactDto: MergeContactDto) {
		const { data } = await axios.post(
			`/contact/contacts/merge`,
			mergeContactDto,
		);
		return data;
	}

	async fetchAllContactsItemsForExport(
		params: TableItemsOptions,
	): Promise<TableDataResponse<Contact>> {
		const url = '/contact/contacts?export=true';
		const { sortBy, sortDesc } = appendUpdatedAtTiebreaker(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchMemberItems(params: TableItemsOptions): Promise<any> {
		const url = '/contact/members';
		const { sortBy, sortDesc } = appendUpdatedAtTiebreaker(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchMemberItemsForExport(
		params: TableItemsOptions,
	): Promise<TableDataResponse<ContactProfile>> {
		const url = '/contact/members?export=true';
		const { sortBy, sortDesc } = appendUpdatedAtTiebreaker(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchVolunteerItems(params: TableItemsOptions): Promise<any> {
		const url = '/contact/volunteers';
		const { sortBy, sortDesc } = appendUpdatedAtTiebreaker(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchParticipantItems(params: TableItemsOptions): Promise<any> {
		const url = '/contact/participants';
		const { sortBy, sortDesc } = appendUpdatedAtTiebreaker(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchJobParticipantItems(params: TableItemsOptions): Promise<any> {
		const url = '/contact/job-participants';
		const { sortBy, sortDesc } = appendUpdatedAtTiebreaker(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchParticipantsForExport(
		params: TableItemsOptions,
	): Promise<TableDataResponse<ContactProfile>> {
		const url = '/contact/participants?export=true';
		const { sortBy, sortDesc } = appendUpdatedAtTiebreaker(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchJobParticipantsForExport(
		params: TableItemsOptions,
	): Promise<TableDataResponse<ContactProfile>> {
		const url = '/contact/job-participants?export=true';
		const { sortBy, sortDesc } = appendUpdatedAtTiebreaker(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchVolunteerItemsForExport(
		params: TableItemsOptions,
	): Promise<TableDataResponse<ContactProfile>> {
		const url = '/contact/volunteers?export=true';
		const { sortBy, sortDesc } = appendUpdatedAtTiebreaker(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchApplicationItems(params: TableItemsOptions): Promise<any> {
		const url = '/contact/applications';
		const { sortBy, sortDesc } = appendUpdatedAtTiebreaker(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async printContactApplication(profileId?: string): Promise<any> {
		const { data } = await axios.get(
			`/contact/contact-profiles/${profileId}/application-form`,
		);
		return data;
	}

	async fetchContacts(conditions?: object): Promise<any> {
		const params: any = {};
		if (conditions) {
			for (const [key, val] of Object.entries(conditions)) {
				params[key] = val;
			}
		}
		const { data } = await axios.get(`/contact/contacts`, { params });
		return data;
	}

	async searchContacts(
		conditions: ContactSearchCondition[],
	): Promise<Contact[]> {
		const { data } = await axios.post(`/contact/contacts/search`, conditions);
		return data;
	}

	async searchContactsByAppUser(
		condition: SearchByAppUserIdCondition,
	): Promise<Contact[]> {
		const { data } = await axios.post(
			`/contact/contacts/search-appuser`,
			condition,
		);
		return data;
	}

	async fetchContact(id: number): Promise<any> {
		const { data } = await axios.get(`/contact/contacts/${id}`);
		return data;
	}

	async createContact(contact: ContactDto) {
		const { data } = await axios.post('/contact/contacts', contact);
		return data;
	}

	async updateContact(id: number, contact: ContactDto): Promise<Contact> {
		const { data } = await axios.put(`/contact/contacts/${id}`, contact);
		return data;
	}

	async deleteContact(contactId: number): Promise<Contact> {
		const { data } = await axios.delete(`/contact/contacts/${contactId}`);
		return data;
	}

	async fetchAllContactProfiles(): Promise<any> {
		const { data } = await axios.get(`/contact/contact-profiles`);
		return data;
	}

	async fetchContactProfilesByContact(
		contactId: number,
	): Promise<ContactProfile[]> {
		const { data } = await axios.get(`/contact/contact-profiles`, {
			params: {
				contactId,
			},
		});
		return data;
	}

	async fetchMainContactProfileByProfile(
		contactProfile: ContactProfile,
	): Promise<ContactProfile> {
		const { data } = await axios.get(
			`/contact/contact-profiles/${contactProfile.id}/main-applicant/`,
		);
		return data;
	}

	async fetchContactProfile(id: number, withDeleted?: boolean): Promise<any> {
		const { data } = await axios.get(`/contact/contact-profiles/${id}`, {
			params: {
				withDeleted,
			},
		});
		return data;
	}

	async updateContactProfile(newProfile: ContactProfileDto) {
		const { data } = await axios.put(
			`/contact/contact-profiles/${newProfile.id}`,
			newProfile,
		);
		return data;
	}

	async updateContactProfileStatusEndDate(
		profileId: number,
		profileStatusId: number,
	) {
		const { data } = await axios.post(
			`/contact/contact-profiles/${profileId}/profile-statuses/${profileStatusId}/extend`,
			{},
		);
		return data;
	}

	async updateContactProfileStatus(
		profileId: number,
		profileStatusId: number,
		dto: { status?: string; reason?: string },
	) {
		const { data } = await axios.put(
			`/contact/contact-profiles/${profileId}/profile-statuses/${profileStatusId}`,
			dto,
		);
		return data;
	}

	async createContactProfile(newProfile: ContactProfileDto) {
		const { data } = await axios.post(`/contact/contact-profiles`, newProfile);
		return data;
	}

	async createContactProfilesForFamilyPlan(newProfiles: ContactProfileDto[]) {
		const { data } = await axios.post(
			`/contact/contact-profiles/family`,
			newProfiles,
		);
		return data;
	}

	async fetchContactProfilesUnderSamePlan(profile: ContactProfile) {
		const { data } = await axios.get('/contact/contact-profiles', {
			params: {
				paymentId: profile.paymentId,
				paymentPlanId: profile.paymentPlanId,
				profileTypeId: profile.profileType!.id,
			},
		});
		return data;
	}

	async fetchContactProfilesUnderSameProfileType(profile: ContactProfile) {
		const { data } = await axios.get('/contact/contact-profiles', {
			params: {
				contactId: profile.contactId,
				profileType: profile.profileType?.category,
			},
		});
		return data;
	}

	async fetchContactProfilesRelatedToAllContactProfiles(paymentIds: string[]) {
		const { data } = await axios.get('/contact/contact-profiles', {
			params: {
				paymentIds: paymentIds,
			},
		});
		return data;
	}

	async fetchContactDictionary(contactType?: ContactTypeCode): Promise<any> {
		const { data } = await axios.get('/contact/dictionaries', {
			params: {
				contactType,
			},
		});
		return data;
	}

	async fetchProfileTypes(
		contactType?: ContactTypeCode,
	): Promise<ProfileType[]> {
		const url = contactType
			? `/contact/profile-types?contactType=${contactType}`
			: '/contact/profile-types';
		const { data } = await axios.get(url);
		return data;
	}

	async fetchFormTypes(): Promise<FormTypeItem[]> {
		const { data: formTypes } = await axios.get('/form/form-types');
		return formTypes;
	}

	async fetchTemplates(): Promise<any> {
		const { data } = await axios.get('/form/form-templates');
		return data;
	}

	async fetchPresetFields(): Promise<any> {
		const { data } = await axios.get('/form/preset-fields');
		return data;
	}

	async fetchCustomFields(): Promise<any> {
		const { data } = await axios.get('/form/custom-fields');
		return data;
	}

	async createTemplate(params: any): Promise<any> {
		const { data } = await axios.post('/form/form-templates', params);
		return data;
	}

	async updateTemplate(params: any): Promise<any> {
		const { data } = await axios.put(
			`/form/form-templates/${params.id}`,
			params,
		);
		return data;
	}

	async deleteTemplate(id: number): Promise<any> {
		const { data } = await axios.delete(`/form/form-templates/${id}`);
		return data;
	}

	async updateTemplateStatus(params: any): Promise<any> {
		const { data } = await axios.post(
			`/form/form-templates/${params.id}/status-changes`,
			params.data,
		);
		return data;
	}

	async fetchPlans(
		category?: ContactTypeCode,
		subCategory?: string,
	): Promise<any> {
		let params: any = {};
		if (category) {
			params = { ...params, category };
		}
		if (subCategory) {
			params = { ...params, subCategory };
		}
		const { data } = await axios.get('/payment/plans', { params });
		return data;
	}

	async fetchPaginatedPlans(options: TableItemsOptions): Promise<any> {
		const url = '/payment/plans';
		const data = fetchTableItems({ ...options, url });
		return data;
	}

	async fetchStripeProducts(params: TableItemsOptions) {
		const url = '/payment/stripe-products';
		const data = fetchTableItems({ ...params, url: url });
		return data;
	}

	async deletePlan(planId: string) {
		const { data } = await axios.delete(`/payment/plans/${planId}`);
		return data;
	}

	async fetchStripeAccounts(): Promise<string[]> {
		const { data } = await axios.get('/payment/accounts/stripe-accounts');
		return data;
	}

	async fetchAllFormsWithOnlyIdAndName(): Promise<any> {
		const { data: forms } = await axios.get(
			'/form/forms?allCentersWithIdAndNameOnly=true',
		);
		return forms;
	}

	async fetchSubmitForm(id: string): Promise<any> {
		const { data } = await axios.get(`/form/submit-forms/${id}`);
		return data;
	}

	async requestContactFileSignedUrl(body: any): Promise<any> {
		const { data } = await axios.post(
			'/contact/file-storage/request-upload-signed-url',
			body,
		);
		return data;
	}

	async uploadImageToGCS(file: File) {
		const { filePath, signedUrl } = await this.requestContactFileSignedUrl({
			filename: file.name,
			contentType: file.type,
		});

		await axios.put(signedUrl, file, {
			headers: { 'Content-Type': file.type },
		});
		return filePath;
	}

	async getContactFileSignedUrl(filePath: string): Promise<any> {
		const { data } = await axios.get('/contact/file-storage/signed-url', {
			params: { filePath },
		});
		return data;
	}

	async fetchPlan(id?: string) {
		const { data } = await axios.get(`/payment/plans/${id}`);
		return data;
	}

	async createPlan(plan: PlanDto) {
		const { data } = await axios.post('/payment/plans', plan);
		return data;
	}

	async updatePlan(plan: PlanDto) {
		const { data } = await axios.put(`/payment/plans/${plan.id}`, plan);
		return data;
	}

	async fetchPayment(id?: string): Promise<Payment> {
		const { data } = await axios.get(`/payment/payments/${id}`);
		return data;
	}

	async fetchStripePaymentDetails(id?: string) {
		const { data } = await axios.get(`/payment/payments/${id}/stripe-details`);
		return data;
	}

	async paymentRefund(
		id: string,
		body: {
			currency: string;
			amount: number;
			remarks?: string;
			paymentMethod: string;
		},
	) {
		body.amount = Math.round(body.amount * 100);
		const { data } = await axios.post(`/payment/payments/${id}/refund`, body);
		return data;
	}

	async voidPayment(
		id: string,
		body: {
			remarks: string;
		},
	) {
		const { data } = await axios.post(`/payment/payments/${id}/void`, body);
		return data;
	}

	async updatePayment(id?: string, body?: { remarks?: string }) {
		const { data } = await axios.put(`/payment/payments/${id}`, body);
		return data;
	}

	async updateReceiptRemarks(id: string, remarks?: string) {
		const { data } = await axios.post(
			`/payment/payments/${id}/receipts/latest/update-remarks`,
			{
				remarks,
			},
		);
		return data;
	}

	async generateReceiptForPreview(
		id: string,
		confirm?: boolean,
	): Promise<{
		receiptId: string;
		receipt: any;
		confirmRequired: boolean;
		message: string;
	}> {
		const { data } = await axios.post(`/payment/payments/${id}/receipts`, {
			confirm,
		});
		return data;
	}

	async confirmReceipt(
		id: string,
		receiptId: string,
		remarks: string,
		voidReason: string,
	) {
		const {
			data,
		} = await axios.post(
			`/payment/payments/${id}/receipts/${receiptId}/confirm`,
			{ remarks, voidReason },
		);
		return data;
	}

	async getReceipt(paymentId?: string, receiptId?: string): Promise<any> {
		const { data } = await axios.get(
			`/payment/payments/${paymentId}/receipts/${receiptId}`,
		);
		return data;
	}

	async downloadReceipt(
		paymentId?: string,
		receiptId?: string,
		printWithoutHeader?: boolean,
	): Promise<any> {
		const { data } = await axios.get(
			`/payment/payments/${paymentId}/receipts/${receiptId}/pdf?withoutHeader=${printWithoutHeader}`,
		);
		return data;
	}

	async fetchPayments(params: TableItemsOptions) {
		const url = `/payment/payments`;
		const { sortBy, sortDesc } = appendPaymentTableSortTiebreakers(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchTransactions(params: TableItemsOptions) {
		const url = `/payment/transactions`;
		const { sortBy, sortDesc } = appendPaymentTableSortTiebreakers(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchTransactionsForExport(params: TableItemsOptions) {
		const url = `/payment/transactions?export=true`;
		const { sortBy, sortDesc } = appendPaymentTableSortTiebreakers(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchPaymentsForExport(params: TableItemsOptions) {
		const url = `/payment/payments?export=true`;
		const { sortBy, sortDesc } = appendPaymentTableSortTiebreakers(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchBankedCashTransactions(params: TableItemsOptions) {
		const url = '/payment/cash-transactions?banked=true';
		const { sortBy, sortDesc } = appendPaymentTableSortTiebreakers(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchNotBankedCashTransactions(params: TableItemsOptions) {
		const url = '/payment/cash-transactions?banked=false';
		const { sortBy, sortDesc } = appendPaymentTableSortTiebreakers(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchBankedCashTransactionsForExport(params: TableItemsOptions) {
		const url = '/payment/cash-transactions?banked=true&export=true';
		const { sortBy, sortDesc } = appendPaymentTableSortTiebreakers(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async fetchNotBankedCashTransactionsForExport(params: TableItemsOptions) {
		const url = '/payment/cash-transactions?banked=false&export=true';
		const { sortBy, sortDesc } = appendPaymentTableSortTiebreakers(
			params.sortBy,
			params.sortDesc,
		);
		const data = fetchTableItems({ ...params, sortBy, sortDesc, url: url });
		return data;
	}

	async undoBankInCashTransactions(transactionIds: string[]) {
		const { data } = await axios.post(`/payment/bank-in-batches/undo`, {
			transactionIds,
		});
		return data;
	}

	async updateBankInRemarks(transactionId: string, bankInRemarks: string) {
		const { data } = await axios.post(
			`/payment/transactions/${transactionId}/update-bank-in-remarks`,
			{
				bankInRemarks,
			},
		);
		return data;
	}

	async bankInCashTransactions(
		bankInDate: string,
		transactionIds: string[],
		isPreview: boolean,
	) {
		const { data } = await axios.post(`/payment/bank-in-batches`, {
			bankInDate,
			transactionIds,
			isPreview,
		});
		return data;
	}

	async payWithId(
		id: string,
		method: string,
		currency: string,
		amount: number,
		remarks: string,
		isRenew: boolean,
		adjustedFee: number | null = null,
		adjustRemarks = '',
	) {
		const { data } = await axios.post(`/payment/payments/${id}/pay`, {
			paymentMethod: method,
			currency,
			amount,
			remarks,
			adjustedFee,
			adjustRemarks,
			isRenew,
		});
		return data;
	}

	async checkContactProfilePaymentStatus(paymentId: string, planId: string) {
		const { data } = await axios.get(
			`/contact/payment-status/auth/${paymentId}/${planId}`,
		);
		return data;
	}

	async fetchTableFilters(code: string): Promise<any> {
		const { data } = await axios.get(`/admin/filters?tableName=${code}`);
		return data;
	}

	async fetchOneFilter(id: number): Promise<any> {
		const { data } = await axios.get(`/admin/filters/${id}`);
		return data;
	}

	async deletOneFilter(id: number): Promise<any> {
		const { data } = await axios.delete(`/admin/filters/${id}`);
		return data;
	}

	async createFilter(params: any): Promise<any> {
		const { data } = await axios.post('/admin/filters', params);
		return data;
	}

	async updateFilter(params: any): Promise<any> {
		const { data } = await axios.put(`/admin/filters/${params.id}`, {
			name: params.name,
			data: params.data,
		});
		return data;
	}

	async updateFilterParams(filterId: number, params: any): Promise<any> {
		const { data } = await axios.put(`/admin/filters/${filterId}/params`, {
			data: params,
		});
		return data;
	}

	async fetchDraftTemplateList(params: TableItemsOptions): Promise<any> {
		const url = '/form/form-templates?status=draft';
		const data = fetchTableItems({ ...params, url: url });
		return data;
	}

	async fetchSubmittedTemplateList(params: TableItemsOptions): Promise<any> {
		const url = '/form/form-templates?status=pending,approved';
		const data = fetchTableItems({ ...params, url: url });
		return data;
	}

	async fetchFormList(params: TableItemsOptions): Promise<any> {
		const url = '/form/forms?active=true';
		const data = fetchTableItems({ ...params, url: url });
		return data;
	}

	async fetchUserItems(params: TableItemsOptions): Promise<any> {
		const url = '/admin/users';
		const data = fetchTableItems({ ...params, url: url });
		return data;
	}

	async fetchAuditLogsItems(params: TableItemsOptions): Promise<any> {
		const url = '/audit/audit-logs';
		const data = fetchTableItems({ ...params, url: url });
		return data;
	}

	async fetchAuditLogIndexItems(params: TableItemsOptions): Promise<any> {
		const url = '/audit/audit-log-index';
		const data = fetchTableItems({ ...params, url: url });
		return data;
	}

	async createUser(user: CreateUserDto): Promise<any> {
		const { data } = await axios.post(`/admin/users`, user);
		return data;
	}

	async updateUser(userId: string, updateUserDto: UpdateUserDto): Promise<any> {
		const { data } = await axios.put(`/admin/users/${userId}`, updateUserDto);
		return data;
	}

	async fetchUsers({
		ngoId,
		centerIds,
		includeAccessible,
		withDeleted,
	}: {
		ngoId?: number;
		centerIds?: number[];
		includeAccessible?: boolean;
		withDeleted?: boolean;
	}): Promise<UserAccount[]> {
		const url = '/admin/users';
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const params: any = {};
		if (ngoId) {
			params.ngoId = ngoId;
		}
		if (centerIds) {
			params.centerIds = centerIds;
		}
		if (includeAccessible) {
			params.includeAccessible = includeAccessible;
		}
		if (withDeleted) {
			params.withDeleted = withDeleted;
		}
		const { data } = await axios.get(url, { params });
		return data;
	}

	async fetchUser(id: string): Promise<UserAccount> {
		const url = `/admin/users/${id}`;
		const { data } = await axios.get(url);
		return data;
	}

	async fetchCenters(
		ngoId: number,
		params?: TableItemsOptions,
		publicOnly?: boolean,
	): Promise<any> {
		let url = `/contact/ngos/${ngoId}/centers`;
		if (params) {
			url = tableItemUrl({ ...params, url: url });
		}
		if (publicOnly) {
			url = url + `&publicOnly=true`;
		}
		const { data } = await axios.get(url);
		return data;
	}

	async fetchUserRoleTemplates(): Promise<UserRoleTemplate[]> {
		const url = `/admin/user-role-templates`;
		const { data } = await axios.get(url);
		return data;
	}

	async fetchUserPermissions(): Promise<UserPermission[]> {
		const url = `/admin/user-permissions`;
		const { data } = await axios.get(url);
		return data;
	}

	async fetchUserRoles(): Promise<UserRole[]> {
		const url = `/admin/user-roles`;
		const { data } = await axios.get(url);
		return data;
	}

	async createUserRole(role: UserRoleDto) {
		const { data } = await axios.post(`/admin/user-roles`, role);
		return data;
	}

	async updateUserRole(id: string, role: UserRoleDto) {
		const { data } = await axios.put(`/admin/user-roles/${id}`, role);
		return data;
	}

	async fetchCauses(): Promise<any> {
		const { data } = await axios.get('/contact/causes');
		return data;
	}

	async fetchRecipients(): Promise<any> {
		const { data } = await axios.get('/contact/recipients');
		return data;
	}

	async fetchJobEventTypes(): Promise<JobEventType[]> {
		const { data } = await axios.get('/job/event-types');
		return data;
	}

	async fetchVolunteerFunctions(): Promise<VolunteerFunction[]> {
		const { data } = await axios.get('/job/volunteer-functions');
		return data;
	}

	async getJobFileSignedUrl(filePath: string): Promise<any> {
		const { data } = await axios.get('/job/file-storage/signed-url', {
			params: { filePath },
		});
		return data;
	}

	async fetchSdgoals(): Promise<any> {
		const { data } = await axios.get('/contact/sdgoals');
		return data;
	}

	async fetchActivityThemes(): Promise<any> {
		const { data } = await axios.get('/event/activity-themes');
		return data;
	}

	async fetchContactPaymentStatuses(): Promise<any> {
		const { data } = await axios.get('/contact/contact-payment-statuses');
		return data;
	}

	async fetchPaymentStatuses(): Promise<any> {
		const { data } = await axios.get('/payment/payment-statuses');
		return data;
	}

	async fetchPaymentMethods(): Promise<any> {
		const { data } = await axios.get('/payment/payment-methods');
		return data;
	}

	async fetchAuditLog(id: string): Promise<any> {
		const url = `/audit/audit-logs/${id}`;
		const { data } = await axios.get(url);
		return data;
	}

	async fetchFamilyRelationsByContact(
		contactId: number,
	): Promise<FamilyRelation[]> {
		const { data } = await axios.get(
			`/contact/contacts/${contactId}/family-relations`,
		);
		return data;
	}

	async createFamilyRelation(
		contactId: number,
		relation: FamilyRelationDto,
	): Promise<FamilyRelation> {
		const { data } = await axios.post(
			`/contact/contacts/${contactId}/family-relations`,
			relation,
		);
		return data;
	}

	async updateFamilyRelation(
		relationId: string,
		relation: FamilyRelationDto,
	): Promise<FamilyRelation> {
		const { data } = await axios.put(
			`/contact/family-relations/${relationId}`,
			relation,
		);
		return data;
	}

	async deleteFamilyRelation(relationId: string): Promise<any> {
		const { data } = await axios.delete(
			`/contact/family-relations/${relationId}`,
		);
		return data;
	}

	async fetchCenter(centerId: number) {
		const { data } = await axios.get(`/contact/centers/${centerId}`);
		return data;
	}

	async createUnit(params: any) {
		const { data } = await axios.post('/contact/centers', params);
		return data;
	}

	async updateUnit(params: any) {
		const { data } = await axios.put(
			`/contact/centers/${params.centerId}`,
			params.unit,
		);
		return data;
	}

	async deleteUnit(centerId: number): Promise<any> {
		const { data } = await axios.delete(`/contact/centers/${centerId}`);
		return data;
	}

	async updateNgo(params: any) {
		const { data } = await axios.put(`/contact/ngos/${params.id}`, params.ngo);
		return data;
	}

	async deleteUser(uuid: string): Promise<any> {
		const { data } = await axios.delete(`/admin/users/${uuid}`);
		return data;
	}

	async updateQRCode(params: any) {
		const { data } = await axios.put(
			`/contact/qr-codes/${params.id}`,
			params.data,
		);
		return data;
	}

	async renewMembership(profileId: number, bodyData: any) {
		const { data } = await axios.post(
			`/contact/contact-profiles/${profileId}/renew`,
			bodyData,
		);
		return data;
	}

	async createContactWithFamilyMembers({
		main,
		dependants,
	}: {
		main: ContactDto;
		dependants: DraftFamilyContactWithRelation[];
	}): Promise<any> {
		const { data } = await axios.post(`/contact/contacts/family`, {
			main,
			dependants,
		});
		return data;
	}

	async fetchNotificationBadges(): Promise<any> {
		const { data } = await axios.get('/notification/badges');
		return data;
	}

	async createConnectInvite(contactId: number, email?: string) {
		const data = await axios.post(
			`/contact/contacts/${contactId}/connect-invite`,
			{
				email,
			},
		);
		return data.data;
	}

	async disconnectAppAccount(contactId: number) {
		const data = await axios.post(`/contact/contacts/${contactId}/disconnect`);
		return data.data;
	}

	async fetchRegions(): Promise<Region[]> {
		const { data } = await axios.get(`/location/regions`);
		return data;
	}

	async fetchDistricts(): Promise<District[]> {
		const { data } = await axios.get(`/location/districts`);
		return data;
	}

	async fetchSubDistricts(): Promise<SubDistrict[]> {
		const { data } = await axios.get(`/location/sub-districts`);
		return data;
	}

	async fetchRegionByCode(code: string): Promise<Region> {
		const { data } = await axios.get(`/location/regions/${code}`);
		return data;
	}

	async fetchDistrictByCode(code: string): Promise<District> {
		const { data } = await axios.get(`/location/districts/${code}`);
		return data;
	}

	async fetchSubDistrictByCode(code: string): Promise<SubDistrict> {
		const { data } = await axios.get(`/location/sub-districts/${code}`);
		return data;
	}

	async proposalCompletion(proposalId: string) {
		await axios.post(`/program/proposals/${proposalId}/completion`);
	}

	async undoProposalCompletion(proposalId: string) {
		await axios.post(`/program/proposals/${proposalId}/undo-completion`);
	}
}
